import React, { useState, memo } from 'react';
/*import Select from 'react-select';*/
//import Select from './BootstrapWrapper'
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import SupplierCreationDialog from './SupplierCreationDialog';
import { FormWrapper } from './Wrappers';
import { Supplier } from './Types';
import { normalizeCurrency } from './CurrencySelect';

type Option = { label: string, value: string, group?: string }

// What the picker shows beside a supplier's name: the account its invoices would be paid to.
//
// This is the only chance to notice that an existing supplier's account is wrong, and once an
// invoice is attested the payment goes out on it. A domestic supplier has always shown its giro
// here; a foreign one showed only its currency, so there was nothing to check at all — the IBAN
// was visible only while creating the supplier, never afterwards.
//
// The IBAN is grouped in fours, the way it is printed on an invoice, so it can be read against the
// paper rather than deciphered. The BIC follows it, since a wrong BIC misroutes a payment as surely
// as a wrong IBAN does.
const groupedIban = (iban: string): string => (iban.match(/.{1,4}/g) ?? [iban]).join(' ')

export function payeeAccount(supplier: Supplier): string {
    if (supplier.GiroNumber) return supplier.GiroNumber
    if (supplier.BankAccountNumber) return `konto ${[supplier.Clearing, supplier.BankAccountNumber].filter(Boolean).join('-')}`
    if (supplier.BankIban) return [groupedIban(supplier.BankIban), supplier.BankBic].filter(Boolean).join(' · ')
    // Neither: Visma accepts a foreign supplier with no account yet (the invoice simply cannot be
    // paid until it has one). Say so rather than leave empty parentheses.
    return `${normalizeCurrency(supplier.CurrencyCode)} · inget konto`
}

// The picker's groups, in the order they are listed: how each supplier is paid.
const GROUPS = ['Bankgiro och plusgiro', 'Bankkonto', 'Utländska'] as const
function payeeGroup(supplier: Supplier): typeof GROUPS[number] {
    if (normalizeCurrency(supplier.CurrencyCode) !== 'SEK') return 'Utländska'
    return supplier.BankAccountNumber ? 'Bankkonto' : 'Bankgiro och plusgiro'
}

// Whether invoices to this supplier are paid to an ordinary bank account — where a payment carries
// a message to the payee, never an OCR number.
export function isPaidToBankAccount(supplierId: string | null, suppliers: Supplier[]): boolean {
    return !!suppliers.find(supplier => supplier.Id === supplierId)?.BankAccountNumber
}

// Valid means a supplier that exists *and* is in the invoice's currency — not merely that
// something was chosen. Visma refuses an invoice whose currency differs from its supplier's, so a
// mismatch is not a preference but a booking that will fail. Checking only "not null" also let the
// empty string through, which is exactly what clearing the field sets it to.
export function isValidSupplier(supplierId: string | null, suppliers: Supplier[],
                                currencyCode?: string): boolean {
  const chosen = suppliers.find(supplier => supplier.Id === supplierId)
  return chosen !== undefined && normalizeCurrency(chosen.CurrencyCode) === normalizeCurrency(currencyCode)
}

const SupplierSelect = memo(({label, suppliers, currencyCode, usesOcr, attachmentId, value, onChange, onChangeCurrency, onChangeSuppliers, id}: {
  label?: string,
  suppliers: Supplier[],
  // Only suppliers in this currency can be picked: Visma rejects an invoice whose currency differs
  // from its supplier's, so offering the others would just fail at booking time.
  currencyCode?: string,
  // Whether the invoice being booked pays by OCR, passed to the creation dialog as the new
  // supplier's starting value. Nothing here couples the picker to the OCR checkbox: the backend
  // matches the supplier's setting to the invoice when it registers it.
  usesOcr?: boolean,
  // Passed to the creation dialog, which reads the account off the invoice itself.
  attachmentId?: string,
  value: string | null,
  onChange: (newValue: string | null) => void,
  // Set the invoice's currency — used when a supplier is created in a different one.
  onChangeCurrency: (newCurrency: string) => void,
  onChangeSuppliers: (newSuppliers: Supplier[]) => void,
  id?: string
}) => {

  const [dialogEnabled, setDialogEnabled] = useState(false)

  const currency = normalizeCurrency(currencyCode)
  const matching = suppliers.filter(supplier => normalizeCurrency(supplier.CurrencyCode) === currency)
  // Grouped by how each supplier is paid, then by name — groupBy needs them in that order.
  const lastOptions: Option[] = matching
    .map(supplier => ({
      value: supplier.Id,
      label: `${supplier.Name} (${payeeAccount(supplier)})`,
      group: payeeGroup(supplier)}))
    .sort((a, b) => GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group) || a.label.localeCompare(b.label, 'sv'))
  // A draft can name a supplier in another currency — saved before the invoice's currency was
  // changed, say. Filtering it out would leave the field looking *empty* while the draft still
  // holds it: nothing to see, nothing to clear, and an attest that fails at Visma. Show it instead,
  // with its own currency named, so the mismatch is on screen and one click from being fixed.
  const mismatched = suppliers.find(
    supplier => supplier.Id === value && normalizeCurrency(supplier.CurrencyCode) !== currency)
  const options: Option[] = [
    { value: 'NEW', label: 'Ny…'},
    ...(mismatched === undefined ? [] : [{
      value: mismatched.Id,
      label: `${mismatched.Name} (${normalizeCurrency(mismatched.CurrencyCode)} — inte fakturans valuta)`}]),
    ...lastOptions
  ]
  const selectedOption = options.find(option => option.value == value) ?? null

  function handleChange(option: Option | null) {
    if (option !== null && option.value == "NEW") {
      setDialogEnabled(true)
    } else {
      const newSupplierId = option === null ? null : option.value
      onChange(newSupplierId)
    }
  }

  function handleCreate({newSuppliers, newSupplierId, currencyCode: created}:
                        {newSuppliers: Supplier[], newSupplierId: string, currencyCode: string}) {
    onChangeSuppliers(newSuppliers)
    // Creating a supplier in another currency means the invoice is in that currency — that is why
    // it was changed in the dialog. Carry it back, or the new supplier is instantly out of the
    // invoice's currency. This must come first: changing the invoice's currency clears the chosen
    // supplier, so selecting the new one has to happen after.
    if (created !== currency) {
      onChangeCurrency(created)
    }
    onChange(newSupplierId)
    setDialogEnabled(false)
  }

  function handleCancel() {
    setDialogEnabled(false)
  }

  return (
    <>
      <TheSelect
        label={label}
        value={selectedOption}
        options={options}
        onChange={(_event, newValue) => {
          handleChange(newValue)
        }}
        id={id}
      />
      {dialogEnabled && <SupplierCreationDialog currencyCode={currency} usesOcr={usesOcr}
                                                attachmentId={attachmentId}
                                                onCreate={handleCreate} onCancel={handleCancel}/>}
    </>
  )
})

const TheSelect = FormWrapper(
  {
    Component:
    ({error, ...props}: {error: boolean, disabled: boolean, sx: {}, id?: string} & Omit<AutocompleteProps<Option, false, false, false>, 'renderInput'>) =>
      <Autocomplete<Option>
        {...props}
        autoHighlight
        groupBy={option => option.group ?? ''}
        renderInput={(params) => <TextField {...params} error={error}/>}
        renderOption={(inner_props, option: Option) => (
          <Box component="li" {...inner_props}>
            {option.value === 'NEW' ? <b>{option.label}</b> : option.label}
          </Box>
        )}
      />,
    validationFunction: (option: Option | null) => option !== null
  }
)

export default SupplierSelect
